import { Head, Link, usePage } from '@inertiajs/react';
import MainLayout from '@/Layouts/MainLayout';
import { useEffect, useRef, useState } from 'react';

function containsSlug(node, slug) {
    return (node.children || []).some(child => child.slug === slug || containsSlug(child, slug));
}

function MenuNode({ node, currentSlug, depth = 0, baseRoute = 'document.show' }) {
    const isActive = node.slug === currentSlug;
    const hasChildren = node.children && node.children.length > 0;
    const isParentOfActive = hasChildren && containsSlug(node, currentSlug);
    const [open, setOpen] = useState(isActive || isParentOfActive);

    return (
        <div>
            <div className={`flex items-center gap-1 ${depth > 0 ? 'ml-4' : ''}`}>
                {hasChildren ? (
                    <button
                        onClick={() => setOpen(o => !o)}
                        className="size-5 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-white flex-shrink-0"
                    >
                        <span className="material-symbols-outlined text-[16px]">
                            {open ? 'expand_more' : 'chevron_right'}
                        </span>
                    </button>
                ) : (
                    <span className="w-5 flex-shrink-0" />
                )}
                <Link
                    href={route(baseRoute, node.slug)}
                    className={`flex-1 text-sm py-1.5 px-2 rounded-lg transition-colors truncate ${
                        node.slug === 'management-agency-setup'
                            ? 'border-2 border-primary bg-primary/5 text-primary font-bold shadow-sm'
                            : isActive
                                ? 'bg-primary/10 text-primary font-semibold'
                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5'
                        } ${node.slug === 'management-agency-setup' ? 'my-1' : ''}`}
                >
                    {node.title}
                </Link>
            </div>
            {hasChildren && open && (
                <div className="mt-0.5 space-y-0.5">
                    {node.children.map(child => (
                        <MenuNode key={child.id} node={child} currentSlug={currentSlug} depth={depth + 1} baseRoute={baseRoute} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AttachmentIcon({ ext }) {
    const icons = {
        json: { icon: 'data_object', color: 'text-yellow-500' },
        pdf: { icon: 'picture_as_pdf', color: 'text-red-500' },
        zip: { icon: 'folder_zip', color: 'text-blue-500' },
        xml: { icon: 'code', color: 'text-green-500' },
    };
    const match = icons[ext?.toLowerCase()] || { icon: 'attach_file', color: 'text-slate-400' };
    return (
        <span className={`material-symbols-outlined text-[28px] ${match.color}`}>{match.icon}</span>
    );
}

export default function DocumentShow({ document, menuTree, baseRoute = 'document.show', editRoute = 'manager.document.edit' }) {
    const { auth } = usePage().props;
    const contentRef = useRef(null);
    const fileExt = (path) => path?.split('.').pop()?.toUpperCase();
    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    useEffect(() => {
        const root = contentRef.current;

        if (!root) {
            return undefined;
        }

        const dom = root.ownerDocument;
        const languageLabels = new Set(['json', 'http', 'html', 'xml', 'bash', 'text', 'javascript', 'js']);
        const normalizeLabel = (value) => value?.replace(/\u00a0/g, ' ').trim().toLowerCase() || '';

        const createCopyButton = (codeText) => {
            const button = dom.createElement('button');
            button.type = 'button';
            button.className = 'doc-copy-button';
            button.setAttribute('aria-label', 'Copy code');
            button.dataset.docCopyText = codeText;
            button.innerHTML = `
                <span class="material-symbols-outlined text-[18px]" aria-hidden="true">content_copy</span>
                <span class="sr-only">Copy code</span>
            `;

            return button;
        };

        root.querySelectorAll('pre').forEach((pre) => {
            if (pre.closest('[data-doc-code-block="true"]')) {
                return;
            }

            let label = 'code';
            let replaceTarget = pre;
            let labelNode = null;

            const parent = pre.parentElement;
            if (parent?.tagName === 'DIV') {
                const children = Array.from(parent.children);
                if (children.length === 2 && children[1] === pre && children[0].tagName === 'DIV') {
                    const parentLabel = normalizeLabel(children[0].textContent);
                    if (parentLabel) {
                        label = parentLabel;
                    }
                    replaceTarget = parent;
                }
            }

            if (replaceTarget === pre) {
                const previousElement = pre.previousElementSibling;
                const previousLabel = normalizeLabel(previousElement?.textContent);

                if (previousElement?.tagName === 'P' && languageLabels.has(previousLabel)) {
                    label = previousLabel;
                    labelNode = previousElement;
                }
            }

            const codeBlock = dom.createElement('div');
            codeBlock.className = 'doc-code-block';
            codeBlock.dataset.docCodeBlock = 'true';

            const header = dom.createElement('div');
            header.className = 'doc-code-block__header';

            const labelEl = dom.createElement('span');
            labelEl.className = 'doc-code-block__label';
            labelEl.textContent = label;

            const codeText = pre.textContent?.replace(/\u00a0/g, ' ') || '';
            const copyButton = createCopyButton(codeText);

            header.append(labelEl, copyButton);

            const clonedPre = pre.cloneNode(true);
            clonedPre.classList.remove('ql-syntax');
            clonedPre.removeAttribute('style');

            clonedPre.querySelectorAll('[style]').forEach((node) => {
                if (node.tagName === 'CODE') {
                    node.removeAttribute('style');
                }
            });

            codeBlock.append(header, clonedPre);

            if (labelNode) {
                labelNode.remove();
            }

            replaceTarget.replaceWith(codeBlock);
        });

        const handleClick = async (event) => {
            if (!(event.target instanceof Element)) {
                return;
            }

            const button = event.target.closest('.doc-copy-button');

            if (!button || !root.contains(button)) {
                return;
            }

            const copyText = button.dataset.docCopyText || '';

            try {
                await navigator.clipboard.writeText(copyText);
                button.dataset.copied = 'true';
                button.innerHTML = `
                    <span class="material-symbols-outlined text-[18px]" aria-hidden="true">check</span>
                    <span class="sr-only">Copied</span>
                `;

                window.setTimeout(() => {
                    button.dataset.copied = 'false';
                    button.innerHTML = `
                        <span class="material-symbols-outlined text-[18px]" aria-hidden="true">content_copy</span>
                        <span class="sr-only">Copy code</span>
                    `;
                }, 1800);
            } catch (error) {
                console.error('Failed to copy code block', error);
            }
        };

        root.addEventListener('click', handleClick);

        return () => {
            root.removeEventListener('click', handleClick);
        };
    }, [document?.content]);

    return (
        <MainLayout showHeader showFooter>
            <Head title={document ? `${document.title} - Documentation` : 'Documentation'} />

            <div className="max-w-[1400px] mx-auto px-4 py-8 sm:px-6">
                <div className="flex min-h-[calc(100vh-200px)] flex-col gap-6 lg:flex-row lg:gap-8">

                    {/* ── Left: Menu ── */}
                    <aside className="w-full flex-shrink-0 lg:w-64">
                        <div className="lg:sticky lg:top-28">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="material-symbols-outlined text-primary text-[22px]">menu_book</span>
                                <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">Documentation</h2>
                            </div>
                            <nav className="space-y-0.5">
                                {menuTree.map(node => (
                                    <MenuNode key={node.id} node={node} currentSlug={document?.slug} baseRoute={baseRoute} />
                                ))}
                                {menuTree.length === 0 && (
                                    <p className="text-xs text-slate-400 py-4">No documentation pages available.</p>
                                )}
                            </nav>
                        </div>
                    </aside>

                    {/* ── Center: Content ── */}
                    <main className="w-full min-w-0 flex-1">
                        {document ? (
                            <article className="documentation-content">
                                <style>{`
                                    .documentation-content {
                                        min-width: 0;
                                        max-width: 100%;
                                    }

                                    .documentation-content .doc-content {
                                        --doc-banner-bg: linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%);
                                        --doc-surface: #ffffff;
                                        --doc-panel: #f8fafc;
                                        --doc-border: #dbe4ee;
                                        --doc-text: #334155;
                                        --doc-heading: #0f172a;
                                        --doc-muted: #64748b;
                                        --doc-muted-strong: #475569;
                                        --doc-surface-strong: #0f172a;
                                        --doc-code-bg: #0f172a;
                                        --doc-code-border: rgba(15, 23, 42, 0.18);
                                        --doc-code-text: #e2e8f0;
                                        color: #0f172a;
                                        min-width: 0;
                                        max-width: 100%;
                                    }

                                    .documentation-content,
                                    .documentation-content .doc-content,
                                    .documentation-content .doc-content * {
                                        box-sizing: border-box;
                                    }

                                    .documentation-content .doc-content img,
                                    .documentation-content .doc-content video,
                                    .documentation-content .doc-content iframe {
                                        max-width: 100%;
                                        height: auto;
                                    }

                                    .documentation-content .doc-content p,
                                    .documentation-content .doc-content li,
                                    .documentation-content .doc-content h1,
                                    .documentation-content .doc-content h2,
                                    .documentation-content .doc-content h3,
                                    .documentation-content .doc-content h4,
                                    .documentation-content .doc-content h5,
                                    .documentation-content .doc-content h6 {
                                        overflow-wrap: anywhere;
                                    }

                                    .dark .documentation-content .doc-content {
                                        --doc-banner-bg: linear-gradient(135deg, #0f172a 0%, #111827 100%);
                                        --doc-surface: rgba(17, 24, 39, 0.72);
                                        --doc-panel: rgba(15, 23, 42, 0.78);
                                        --doc-border: rgba(148, 163, 184, 0.18);
                                        --doc-text: #e2e8f0;
                                        --doc-heading: #f8fafc;
                                        --doc-muted: #94a3b8;
                                        --doc-muted-strong: rgba(226, 232, 240, 0.78);
                                        --doc-surface-strong: #0f172a;
                                        --doc-code-bg: #020617;
                                        --doc-code-border: rgba(148, 163, 184, 0.2);
                                        --doc-code-text: #e2e8f0;
                                        color: #f8fafc;
                                    }

                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] {
                                        background: var(--doc-banner-bg) !important;
                                        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
                                    }

                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"],
                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] h1,
                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] h2,
                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] h3,
                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] h4,
                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] p,
                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] span,
                                    .documentation-content .doc-content [style*="background:var(--doc-banner-bg)"] code {
                                        color: #ffffff !important;
                                    }

                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] {
                                        background: var(--doc-surface) !important;
                                        border-color: var(--doc-border) !important;
                                        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
                                    }

                                    .documentation-content .doc-content [style*="background:var(--doc-panel)"] {
                                        background: var(--doc-panel) !important;
                                        border-color: var(--doc-border) !important;
                                    }

                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] {
                                        box-shadow: none;
                                    }

                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color:#fff"],
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color: #fff"],
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color:#ffffff"],
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color: #ffffff"],
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] h1,
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] h2,
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] h3,
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] h4,
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] h5,
                                    .documentation-content .doc-content [style*="background:var(--doc-surface)"] h6 {
                                        color: #0f172a !important;
                                    }

                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color:#fff"],
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color: #fff"],
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color:#ffffff"],
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] [style*="color: #ffffff"],
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] h1,
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] h2,
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] h3,
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] h4,
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] h5,
                                    .dark .documentation-content .doc-content [style*="background:var(--doc-surface)"] h6 {
                                        color: #ffffff !important;
                                    }

                                    .documentation-content .doc-content [style*="color:var(--doc-muted-strong)"] {
                                        color: var(--doc-muted-strong) !important;
                                    }

                                    .documentation-content .doc-content [style*="color:var(--doc-text)"] {
                                        color: var(--doc-text) !important;
                                    }

                                    .documentation-content .doc-content [style*="color:var(--doc-heading)"] {
                                        color: var(--doc-heading) !important;
                                    }

                                    .documentation-content .doc-content [style*="color:var(--doc-muted)"] {
                                        color: var(--doc-muted) !important;
                                    }

                                    .documentation-content .doc-content [style*="color:var(--doc-surface-strong)"] {
                                        color: var(--doc-surface-strong) !important;
                                    }

                                    .documentation-content .doc-content pre,
                                    .documentation-content .doc-content [style*="white-space:pre"],
                                    .documentation-content .doc-content [style*="white-space: pre"] {
                                        background: var(--doc-code-bg) !important;
                                        color: var(--doc-code-text) !important;
                                        border: 1px solid var(--doc-code-border) !important;
                                        border-radius: 16px !important;
                                        padding: 18px 20px !important;
                                        overflow-x: auto !important;
                                        margin: 1.5rem 0 !important;
                                    }

                                    .documentation-content .doc-content pre code,
                                    .documentation-content .doc-content pre *,
                                    .documentation-content .doc-content [style*="white-space:pre"] *,
                                    .documentation-content .doc-content [style*="white-space: pre"] * {
                                        background: transparent !important;
                                        color: inherit !important;
                                    }

                                    .documentation-content .doc-content :not(pre) > code,
                                    .documentation-content .doc-content code[style*="background"] {
                                        background: #e2e8f0 !important;
                                        color: #0f172a !important;
                                        border-radius: 6px !important;
                                        padding: 0.15rem 0.4rem !important;
                                    }

                                    .dark .documentation-content .doc-content :not(pre) > code,
                                    .dark .documentation-content .doc-content code[style*="background"] {
                                        background: rgba(148, 163, 184, 0.18) !important;
                                        color: #e2e8f0 !important;
                                    }

                                    .documentation-content .doc-content h1,
                                    .documentation-content .doc-content h2,
                                    .documentation-content .doc-content h3,
                                    .documentation-content .doc-content h4,
                                    .documentation-content .doc-content h5,
                                    .documentation-content .doc-content h6 {
                                        color: var(--doc-heading);
                                    }

                                    .documentation-content .doc-content h1 strong + strong,
                                    .documentation-content .doc-content h2 strong + strong,
                                    .documentation-content .doc-content h3 strong + strong,
                                    .documentation-content .doc-content h4 strong + strong,
                                    .documentation-content .doc-content p strong + strong {
                                        margin-left: 0.4rem;
                                    }

                                    .documentation-content .doc-content table {
                                        width: 100%;
                                        border-collapse: collapse;
                                    }

                                    .documentation-content .doc-content th,
                                    .documentation-content .doc-content td {
                                        vertical-align: top;
                                    }

                                    .documentation-content .doc-content .doc-code-block {
                                        margin: 1.25rem 0;
                                        border: 1px solid var(--doc-code-border);
                                        border-radius: 14px;
                                        overflow: hidden;
                                        background: var(--doc-code-bg);
                                        box-shadow: 0 14px 30px rgba(2, 6, 23, 0.16);
                                    }

                                    .dark .documentation-content .doc-content .doc-code-block {
                                        box-shadow: none;
                                    }

                                    .documentation-content .doc-content .doc-code-block__header {
                                        display: flex;
                                        align-items: center;
                                        justify-content: space-between;
                                        gap: 12px;
                                        padding: 10px 14px;
                                        border-bottom: 1px solid var(--doc-code-border);
                                        background: rgba(15, 23, 42, 0.72);
                                    }

                                    .documentation-content .doc-content .doc-code-block__label {
                                        color: #94a3b8;
                                        font-size: 11px;
                                        font-weight: 700;
                                        letter-spacing: 0.08em;
                                        text-transform: uppercase;
                                    }

                                    .documentation-content .doc-content .doc-copy-button {
                                        display: inline-flex;
                                        align-items: center;
                                        justify-content: center;
                                        width: 32px;
                                        height: 32px;
                                        border-radius: 8px;
                                        border: 1px solid rgba(148, 163, 184, 0.22);
                                        background: rgba(15, 23, 42, 0.42);
                                        color: #cbd5e1;
                                        transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
                                    }

                                    .documentation-content .doc-content .doc-copy-button:hover {
                                        background: rgba(59, 130, 246, 0.18);
                                        border-color: rgba(96, 165, 250, 0.38);
                                        color: #ffffff;
                                    }

                                    .documentation-content .doc-content .doc-copy-button[data-copied="true"] {
                                        background: rgba(34, 197, 94, 0.16);
                                        border-color: rgba(74, 222, 128, 0.3);
                                        color: #86efac;
                                    }

                                    .documentation-content .doc-content .doc-code-block pre {
                                        margin: 0 !important;
                                        border: none !important;
                                        border-radius: 0 !important;
                                        padding: 16px 18px !important;
                                        box-shadow: none !important;
                                        background: transparent !important;
                                    }

                                    @media (max-width: 767px) {
                                        .documentation-content {
                                            overflow-x: hidden;
                                        }

                                        .documentation-content .doc-content {
                                            overflow-x: hidden;
                                        }

                                        .documentation-content .doc-content > *,
                                        .documentation-content .doc-content div {
                                            max-width: 100% !important;
                                            min-width: 0 !important;
                                        }

                                        .documentation-content .doc-content [style*="display:flex"],
                                        .documentation-content .doc-content [style*="display: flex"] {
                                            flex-wrap: wrap !important;
                                        }

                                        .documentation-content .doc-content [style*="display:grid"],
                                        .documentation-content .doc-content [style*="display: grid"],
                                        .documentation-content .doc-content [style*="grid-template-columns"] {
                                            grid-template-columns: minmax(0, 1fr) !important;
                                        }

                                        .documentation-content .doc-content table {
                                            display: block;
                                            width: 100%;
                                            max-width: 100%;
                                            overflow-x: auto;
                                            -webkit-overflow-scrolling: touch;
                                        }

                                        .documentation-content .doc-content th,
                                        .documentation-content .doc-content td {
                                            min-width: 160px;
                                        }

                                        .documentation-content .doc-content pre,
                                        .documentation-content .doc-content .doc-code-block,
                                        .documentation-content .doc-content .doc-code-block pre {
                                            max-width: 100%;
                                            overflow-x: auto !important;
                                            -webkit-overflow-scrolling: touch;
                                        }
                                    }
                                `}</style>
                                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <h1 className="break-words text-3xl font-bold leading-tight text-slate-900 dark:text-white">
                                        {document.title}
                                    </h1>
                                    {auth?.user?.role === 'super_admin' && (
                                        <Link
                                            href={route(editRoute, document.id)}
                                            className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-primary dark:border-white/10 dark:text-slate-400 sm:ml-4"
                                        >
                                            <span className="material-symbols-outlined text-[15px]">edit</span>
                                            Edit
                                        </Link>
                                    )}
                                </div>
                                <div
                                    ref={contentRef}
                                    className="doc-content prose prose-slate dark:prose-invert max-w-none
                                        prose-headings:font-bold
                                        prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
                                        prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                                        prose-code:bg-transparent prose-code:p-0 prose-code:before:content-none prose-code:after:content-none
                                        prose-pre:bg-transparent prose-pre:p-0 prose-pre:m-0
                                        prose-blockquote:border-l-primary prose-blockquote:text-slate-600 dark:prose-blockquote:text-slate-400
                                        prose-img:rounded-xl"
                                    dangerouslySetInnerHTML={{ __html: document.content }}
                                />
                            </article>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-96 text-center">
                                <span className="material-symbols-outlined text-6xl text-slate-200 dark:text-white/10 mb-4">menu_book</span>
                                <p className="text-slate-500 dark:text-slate-400">Select a document from the menu.</p>
                            </div>
                        )}
                    </main>

                    {/* ── Right: Attachments ── */}
                    {document?.attachments?.length > 0 && (
                        <aside className="w-full flex-shrink-0 lg:w-56">
                            <div className="lg:sticky lg:top-28">
                                <h2 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                                    Downloads
                                </h2>
                                <div className="space-y-2">
                                    {document.attachments.map(att => (
                                        <a
                                            key={att.id}
                                            href={att.file_path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                            className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#181410] hover:border-primary/40 hover:bg-primary/5 dark:hover:bg-primary/5 transition-all group"
                                        >
                                            <AttachmentIcon ext={fileExt(att.file_path)} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-800 dark:text-white leading-snug group-hover:text-primary transition-colors truncate">
                                                    {att.title}
                                                </p>
                                                <p className="text-xs text-slate-400 mt-0.5">
                                                    {fileExt(att.file_path)} {att.file_size ? `· ${formatSize(att.file_size)}` : ''}
                                                </p>
                                            </div>
                                            <span className="material-symbols-outlined text-[18px] text-slate-300 dark:text-white/20 group-hover:text-primary transition-colors flex-shrink-0 mt-0.5">
                                                download
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    )}
                </div>
            </div>
        </MainLayout>
    );
}
